import React, { useState, useEffect, useCallback, useRef } from 'react';
import { HiServer, HiCheckCircle, HiXCircle, HiExclamationCircle } from 'react-icons/hi';
import { FiHardDrive, FiCpu } from 'react-icons/fi';

const ServidoresAWS = () => {
  const [serverData, setServerData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const isFetchingRef = useRef(false); // ✅ Previne chamadas simultâneas

  const fetchServerData = useCallback(async () => {
    // ✅ Previne chamadas simultâneas usando ref
    if (isFetchingRef.current) return;
    
    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); 
      
      const response = await fetch('https://v1-csapi.compsoft.com.br:823/aws', {
        headers: {
          Authorization: 'Basic ' + btoa('Portal:Portal1163')
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      setServerData(data);
      console.log('✅ Dados servidores AWS atualizados:', new Date().toLocaleTimeString());
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('⏱️ Timeout ao buscar dados dos servidores AWS');
        setError('Timeout na conexão');
      } else {
        console.error('❌ Error fetching server data:', error);
        setError('Erro ao carregar dados');
      }
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, []); // ✅ Array vazio - função estável, não será recriada

  // ✅ Gerenciar o intervalo de atualização
  useEffect(() => {
    // Busca inicial
    fetchServerData();

    // Configura intervalo (3 minutos)
    intervalRef.current = setInterval(fetchServerData, 3 * 60 * 1000);

    // ✅ Cleanup correto
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchServerData]); // fetchServerData não muda mais

  const getStatusColor = useCallback((status) => {
    const colors = {
      'ON': '#10b981',
      'OFF': '#ef4444',
      'ERROR': '#f59e0b'
    };
    return colors[status] || '#6b7280';
  }, []);

  const getStatusIcon = useCallback((status) => {
    const iconProps = { size: 16, style: { marginRight: '4px' } };
    switch(status) {
      case 'ON':
        return <HiCheckCircle {...iconProps} color="#10b981" />;
      case 'OFF':
        return <HiXCircle {...iconProps} color="#ef4444" />;
      case 'ERROR':
        return <HiExclamationCircle {...iconProps} color="#f59e0b" />;
      default:
        return <HiServer {...iconProps} color="#6b7280" />;
    }
  }, []);

  const getLivreColor = useCallback((livreText) => {
    const livreMatch = livreText.match(/(\d*[,.]?\d+)/);
    const livreValue = livreMatch ? parseFloat(livreMatch[0].replace(',', '.')) : null;

    if (livreValue !== null) {
      if (livreValue < 1.0) return '#ef4444';
      if (livreValue < 2.0) return '#f59e0b';
      return '#10b981';
    }
    
    return '#6b7280';
  }, []);

  const formatDiskText = (text) => {
    return text
      .replace('Espaço Total:', '')
      .replace('Espaço Livre:', '')
      .trim();
  };

  const formatMemoryText = (memTotal, memLivre) => {
    if (!memTotal && !memLivre) return '';
    const total = memTotal?.replace('Memória Total:', '').trim() || '';
    const livre = memLivre?.replace('Memória Livre:', '').trim() || '';
    return { livre, total };
  };

  if (error) {
    return (
      <div style={{ padding: '8px', fontSize: 11, color: '#ef4444', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <HiExclamationCircle size={14} />
        {error}
      </div>
    );
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        {serverData.map((server, index) => {
          const diskText = formatDiskText(server.Total);
          const diskLivre = formatDiskText(server.Livre);
          const memoriaData = formatMemoryText(server.MemoriaTotal, server.MemoriaLivre);
          const diskColor = getLivreColor(server.Livre);
          const memColor = getLivreColor(server.MemoriaLivre || '');
          
          return (
            <tr key={server.servidor || index} style={{ 
              borderBottom: '1px solid #e5e7eb',
              transition: 'background-color 0.2s'
            }}>
              <td style={{ 
                padding: '6px 8px', 
                fontFamily: 'system-ui, -apple-system, sans-serif', 
                fontSize: 12,
                fontWeight: 500,
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <HiServer size={14} color="#6b7280" />
                {server.servidor}
              </td>
              <td style={{ padding: '4px 8px' }}>
                <div style={{ fontSize: 10, lineHeight: 1.5 }}>
                  {/* Espaço em Disco */}
                  <div style={{ color: diskColor, marginBottom: 4, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FiHardDrive size={12} />
                    <span>{diskLivre} / {diskText}</span>
                  </div>
                  {/* Memória */}
                  {memoriaData.livre && (
                    <div style={{ color: memColor, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <FiCpu size={12} />
                      <span>{memoriaData.livre} / {memoriaData.total}</span>
                    </div>
                  )}
                </div>
              </td>
              <td style={{ 
                padding: '4px 12px', 
                fontFamily: 'system-ui, -apple-system, sans-serif', 
                fontSize: 11,
                fontWeight: 600,
                color: getStatusColor(server.status),
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px'
              }}>
                {getStatusIcon(server.status)}
                {server.status}
              </td>
            </tr>
          );
        })}
        {isLoading && serverData.length === 0 && (
          <tr>
            <td colSpan="3" style={{ padding: '20px', textAlign: 'center', fontSize: 12, color: '#6b7280' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <div style={{ 
                  width: '12px', 
                  height: '12px', 
                  border: '2px solid #e5e7eb',
                  borderTop: '2px solid #6b7280',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Carregando...
              </div>
            </td>
          </tr>
        )}
        {!isLoading && serverData.length === 0 && !error && (
          <tr>
            <td colSpan="3" style={{ padding: '20px', textAlign: 'center', fontSize: 12, color: '#6b7280' }}>
              Nenhum dado disponível
            </td>
          </tr>
        )}
      </tbody>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </table>
  );
};

export default ServidoresAWS;